import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  DialogContent,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Check, ContentCopy, OpenInNew } from '@mui/icons-material';
import { FaDiscord } from 'react-icons/fa';
import DialogWindow from '../_misc/DialogWindow';
import FadeContent from '../reactBits/FadeContent';
import SpotlightCard from '../reactBits/SpotlightCard';
import { brand } from '@/theme/foundations/brand';
import {
  CopyScene,
  DiscordScene,
  HandoffArrow,
  useSupportStory,
} from './SupportStory';
import './support-error.css';

export const SUPPORT_DISCORD_URL = 'https://discord.gg/srd3rH6WYU';

/** Also usable outside transfers. reportText MUST already be privacy-filtered. */
export interface SupportErrorDialogProps {
  reportText: string;
  onClose: () => void;
  ariaLabel?: string;
  safetyNote?: string;
}

async function copyReport(text: string, isActive: () => boolean) {
  try {
    if (!navigator.clipboard?.writeText)
      throw new Error('Clipboard unavailable');
    await navigator.clipboard.writeText(text);
    return;
  } catch {
    // A rejected OS permission prompt can arrive after dismissal/replacement.
    // Do not steal focus or attempt another copy in a different screen.
    if (!isActive()) return;
    // Compatibility fallback, on explicit user action only. Never leave a raw
    // report viewer mounted in the dialog or log clipboard exceptions.
    const previous = document.activeElement;
    const field = document.createElement('textarea');
    field.value = text;
    field.readOnly = true;
    field.tabIndex = -1;
    field.setAttribute('aria-hidden', 'true');
    field.style.cssText =
      'position:fixed;opacity:0;pointer-events:none;left:-10000px;top:0';
    document.body.appendChild(field);
    try {
      field.select();
      if (!document.execCommand?.('copy'))
        throw new Error('Clipboard unavailable');
    } finally {
      field.remove();
      if (previous instanceof HTMLElement)
        previous.focus({ preventScroll: true });
    }
  }
}

export default function SupportErrorDialog({
  reportText,
  onClose,
  ariaLabel = 'Support report',
  safetyNote,
}: SupportErrorDialogProps) {
  const theme = useTheme();
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [copyState, setCopyState] = useState<
    'idle' | 'pending' | 'copied' | 'failed'
  >('idle');
  const [replay, setReplay] = useState(0);
  const busy = useRef(false);
  const alive = useRef(true);
  const art = useSupportStory(replay, reduced);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const copy = async () => {
    if (busy.current) return;
    busy.current = true;
    setCopyState('pending');
    try {
      await copyReport(reportText, () => alive.current);
      if (alive.current) {
        setCopyState('copied');
        setReplay((value) => value + 1);
      }
    } catch {
      if (alive.current) setCopyState('failed');
    } finally {
      busy.current = false;
    }
  };
  return (
    <DialogWindow
      open
      onClose={onClose}
      title="Let’s get this sorted"
      hideExpand
      autoFullscreen={false}
      fullWidth
      maxWidth="md"
      // Background progress toasts must never obscure the support actions.
      sx={{ zIndex: theme.zIndex.snackbar + 1 }}
      slotProps={{
        paper: {
          'aria-label': ariaLabel,
          sx: {
            maxWidth: 760,
            width: 'calc(100% - 32px)',
            m: 2,
            maxHeight: 'min(680px, calc(100dvh - 48px))',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          },
        },
      }}
    >
      <DialogContent
        className="support-dialog"
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2.5,
          '--support-accent': theme.palette.primary.main,
          '--support-ink': theme.palette.text.primary,
          '--support-muted': theme.palette.text.secondary,
          '--support-line': theme.palette.divider,
          '--support-paper': theme.palette.background.paper,
          '--support-deep': theme.palette.background.default,
          '--support-wash': alpha(theme.palette.primary.main, 0.08),
          '--support-accent-soft': alpha(theme.palette.primary.main, 0.2),
          '--support-success': theme.palette.success.main,
          '--support-warning': theme.palette.warning.main,
          '--support-discord': brand.discord.main,
          '--support-discord-soft': brand.discord.tint,
          '--support-on-accent': theme.palette.primary.contrastText,
          '--support-sheen': alpha(theme.palette.common.white, 0.25),
        }}
      >
        <FadeContent className="support-entrance" playOnMount duration={500}>
          <Box className="support-intro">
            <Typography component="p" className="support-eyebrow">
              A SMALL DETOUR. A BETTER STUDIO VAULT.
            </Typography>
            <Typography color="text.secondary">
              We’re still in beta, so bumps happen. Share a report with our
              Discord crew to help us investigate.
            </Typography>
          </Box>
          <div
            ref={art}
            className="support-story"
            data-motion={reduced ? 'still' : 'animated'}
          >
            <section className="support-step">
              <div className="support-art">
                <CopyScene />
              </div>
              <div className="support-step-heading">
                <span>01</span>
                <Typography component="h3">Copy the clues</Typography>
              </div>
              <Typography className="support-step-copy" color="text.secondary">
                The useful details. Without your file names.
              </Typography>
              <SpotlightCard className="support-action-wrap">
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => void copy()}
                  disabled={copyState === 'pending'}
                  className="support-copy-button"
                  data-testid="copy-support-report"
                  startIcon={
                    copyState === 'copied' ? <Check /> : <ContentCopy />
                  }
                >
                  {copyState === 'copied'
                    ? 'Report copied — thank you!'
                    : copyState === 'pending'
                      ? 'Copying report…'
                      : 'Copy diagnostic report'}
                </Button>
              </SpotlightCard>
            </section>
            <HandoffArrow />
            <section className="support-step">
              <div className="support-art">
                <DiscordScene />
              </div>
              <div className="support-step-heading">
                <span>02</span>
                <Typography component="h3">Bring in the crew</Typography>
              </div>
              <Typography className="support-step-copy" color="text.secondary">
                Paste it in Discord. We’ll take a look.
              </Typography>
              <SpotlightCard className="support-action-wrap support-discord-wrap">
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  href={SUPPORT_DISCORD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<FaDiscord />}
                  endIcon={<OpenInNew />}
                  className="support-discord-button"
                >
                  Open Discord community
                </Button>
              </SpotlightCard>
            </section>
          </div>
          <div className="support-footnote">
            <Typography
              role="status"
              aria-live="polite"
              className="support-copy-status"
              color={copyState === 'failed' ? 'warning.main' : 'text.secondary'}
            >
              {copyState === 'copied'
                ? 'Copied. You’re ready to paste it into Discord.'
                : copyState === 'failed'
                  ? 'Clipboard unavailable. Try again, or ask us in Discord.'
                  : 'Names and locations are masked. Nothing is sent automatically.'}
            </Typography>
            {safetyNote && (
              <Typography variant="caption" color="text.secondary">
                {safetyNote}
              </Typography>
            )}
          </div>
        </FadeContent>
      </DialogContent>
    </DialogWindow>
  );
}
